import React, { useEffect } from 'react';
import usePayoutStore from '../../../store/payout';
import BaseLabelText from '../../base/label-text';
import BaseInfo from '../../base/info';
import { ICollectionAccount } from '../../../models/payout.model';
import AppFileUpload from "../../base/app-file-upload";

interface DocumentsUploadProps {
  onFileUpload: (
    uploaded: boolean,
    file: File | null,
    base64: string | null,
  ) => void;
  onFileDelete: () => void;
}

const DocumentsUpload: React.FC<DocumentsUploadProps> = ({
  onFileUpload,
  onFileDelete,
}) => {
  const {
    getOnrampingAccounts,
    payoutCollectionAccount,
    setPayoutCollectionAccount,
    payoutPayload,
  } = usePayoutStore();

  const fetchCollectionAccount = async () => {
    const response = await getOnrampingAccounts();
    const collectionAccounts: ICollectionAccount[] =
      response && response.isSuccess ? response?.value : [];
    const filteredData = collectionAccounts.filter(
      (account: ICollectionAccount) =>
        account.currencyCode === payoutPayload.fromCurrencyCode &&
        account.maximumAmount >= payoutPayload.amount &&
        account.enabled,
    );
    setPayoutCollectionAccount(filteredData[0]);
  };

  useEffect(() => {
    fetchCollectionAccount();
  }, [payoutPayload.toCurrencyCode]);

  const collectionAccountDetails = payoutCollectionAccount.id
    ? [
        {
          label: 'Bank Name',
          value: payoutCollectionAccount.bankName,
          copyable: true,
        },
        {
          label: 'Account Number',
          value: payoutCollectionAccount.accountNumber,
          copyable: true,
        },
        {
          label: 'Account Name',
          value: payoutCollectionAccount.accountName,
          copyable: true,
        },
        {
          label: 'Currency',
          value: payoutCollectionAccount.currencyCode,
          copyable: false,
        },
      ]
    : [];

  const handleFileChange = (data: {
    file: File | null;
    base64: string | null;
  }) => {
    if (data.file) {
      onFileUpload(!!data.file, data.file, data.base64);
    } else {
      onFileDelete();
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-gray-text text-sm">Documents Upload</p>
      </div>

      <div className="flex flex-col gap-8 rounded-xl border border-[#F1F2F4] p-2">
        <div className="flex flex-col gap-4 py-1 px-2">
          <p className="text-[#1F2937] font-semibold text-lg">
            {payoutPayload.fromCurrencyCode} Collection Account Details
          </p>
          <div className="flex gap-6">
            {collectionAccountDetails.length ? (
              collectionAccountDetails.map((detail, index) => (
                <BaseLabelText
                  key={index}
                  label={detail.label}
                  value={detail.value}
                  type="vertical-reverse"
                  allowCopy
                />
              ))
            ) : (
              <p className="text-gray-500">
                No collection account available for this payout.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-[#F1F2F4] p-8 gap-8">
        <div className="grid grid-cols-2 gap-4">
          <AppFileUpload
            id={'payout-document'}
            type={'file'}
            label="Payment Receipt"
            onFileChange={handleFileChange}
            maxSize={4}
          />
        </div>
        <BaseInfo
          type="warning"
          text="Please include the reference code when making your transfer to
            ensure proper reconciliation."
        />
      </div>
    </div>
  );
};
export default DocumentsUpload;
